import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { ApiError } from './api/client';
import Ornament from './components/Ornament';
import Icon from './components/Icon';
import './Enroll.css';

const EMPTY = {
    phone: '', phone_consent: false,
    photo_url: '', photo_consent: false,
    date_of_birth: '', gender: '', nationality: '',
    location: { country: '', state: '', city: '' },
    languages: [],
    veda_shakha: '', sampradaya: '', parampara: '', gotra: '',
    highest_qualification: '', field_of_study: '', institution: '', year_completed: '',
    current_status: 'seeking',
    years_experience: '',
    headline: '', bio: '',
    skills: [],
    resume_url: '', linkedin_url: '',
    job_types: [],
    available_from: '',
    min_compensation: '', compensation_currency: 'INR',
    willing_to_relocate: false, willing_to_remote: true,
    accepts_terms: false,
};

const STATUS_OPTIONS  = ['working', 'studying', 'seeking', 'unemployed', 'self-employed', 'retired'];
const GENDER_OPTIONS  = ['Female', 'Male', 'Other', 'Prefer not to say'];
const JOB_TYPE_PILLS  = ['Full-time', 'Part-time', 'Contract', 'Event-based', 'Volunteer', 'Internship'];
const QUAL_OPTIONS    = ['Saṁhitā / Pada / Krama / Ghana', 'Bhāṣya', 'Bachelor\'s', 'Master\'s', 'Doctorate',
                         'Diploma', 'Professional certification', 'School', 'Other'];

const EnrollCandidate = () => {
    const [form, setForm] = useState(EMPTY);
    const [tagInput, setTagInput] = useState({ skills: '', languages: '' });
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState('');
    const [error, setError] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/signin?next=' + encodeURIComponent('/enroll/candidate'));
            return;
        }
        // Pre-fill from existing profile if present
        api.get('/api/enroll/me')
            .then((data) => {
                if (data?.candidate) {
                    setForm((prev) => ({
                        ...prev,
                        ...Object.fromEntries(
                            Object.entries(data.candidate).filter(([k, v]) => v !== null && k in prev)
                        ),
                        location:  data.candidate.location  || prev.location,
                        languages: data.candidate.languages || [],
                        skills:    data.candidate.skills    || [],
                        job_types: data.candidate.job_types || [],
                    }));
                }
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [navigate]);

    const set = (key, value) => setForm((p) => ({ ...p, [key]: value }));
    const setLoc = (key, value) => setForm((p) => ({ ...p, location: { ...p.location, [key]: value } }));

    const togglePill = (key, value) => setForm((p) => ({
        ...p,
        [key]: p[key].includes(value) ? p[key].filter((x) => x !== value) : [...p[key], value],
    }));

    const addTag = (key) => {
        const v = tagInput[key].trim();
        if (!v || form[key].includes(v)) { setTagInput({ ...tagInput, [key]: '' }); return; }
        set(key, [...form[key], v]);
        setTagInput({ ...tagInput, [key]: '' });
    };
    const removeTag = (key, value) => set(key, form[key].filter((x) => x !== value));

    const submit = async (e) => {
        e.preventDefault();
        setError(''); setSuccess('');
        if (!form.accepts_terms) { setError('Please accept the terms to continue.'); return; }
        setSubmitting(true);
        try {
            const payload = {
                ...form,
                year_completed:   form.year_completed ? parseInt(form.year_completed, 10) : null,
                years_experience: form.years_experience ? parseInt(form.years_experience, 10) : null,
                min_compensation: form.min_compensation ? parseFloat(form.min_compensation) : null,
                date_of_birth:    form.date_of_birth || null,
                available_from:   form.available_from || null,
            };
            await api.post('/api/enroll/candidate', payload);
            setSuccess('Profile saved. You can return any time to refine it.');
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } catch (err) {
            setError(err instanceof ApiError ? err.message : 'Failed to save. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) return <div className="enroll-page"><div className="enroll-shell sw-text-muted">Loading…</div></div>;

    return (
        <div className="enroll-page">
            <div className="enroll-shell">

                <header className="enroll-header">
                    <Ornament variant="rule" tone="gold" size={120} style={{ margin: '0 auto 12px', display: 'block' }} />
                    <span className="enroll-eyebrow">Need a Job? Enrol as a Candidate</span>
                    <h1>Your Samudyoga profile</h1>
                    <p>
                        Fields you can leave blank if you'd rather. The more you share, the better
                        we can introduce you to the right opportunity. Your contact details are
                        never disclosed publicly — only via a verified introduction.
                    </p>
                </header>

                <form className="enroll-form" onSubmit={submit}>

                    {/* ─── 1. Contact & identity ─── */}
                    <div className="enroll-section">
                        <div className="enroll-section-title">
                            <span className="enroll-section-num">1</span>
                            <h2>Contact &amp; identity</h2>
                        </div>
                        <p className="enroll-section-hint">
                            Phone and photo are private by default — toggle the consents below to share them.
                        </p>
                        <div className="enroll-row">
                            <div className="enroll-field">
                                <label>Phone</label>
                                <input type="tel" value={form.phone}
                                       onChange={(e) => set('phone', e.target.value)}
                                       placeholder="+91 ..." />
                            </div>
                            <div className="enroll-field">
                                <label>Date of birth</label>
                                <input type="date" value={form.date_of_birth}
                                       onChange={(e) => set('date_of_birth', e.target.value)} />
                            </div>
                        </div>
                        <div className="enroll-row">
                            <div className="enroll-field">
                                <label>Gender</label>
                                <select value={form.gender} onChange={(e) => set('gender', e.target.value)}>
                                    <option value="">— select —</option>
                                    {GENDER_OPTIONS.map((g) => <option key={g} value={g}>{g}</option>)}
                                </select>
                            </div>
                            <div className="enroll-field">
                                <label>Nationality</label>
                                <input type="text" value={form.nationality}
                                       onChange={(e) => set('nationality', e.target.value)}
                                       placeholder="Indian" />
                            </div>
                        </div>

                        <div className="enroll-checkbox-row">
                            <input type="checkbox" id="phone_consent" checked={form.phone_consent}
                                   onChange={(e) => set('phone_consent', e.target.checked)} />
                            <label htmlFor="phone_consent">
                                I consent to share my phone with employers <strong>only after a mutual introduction</strong>.
                            </label>
                        </div>
                    </div>

                    {/* ─── 2. Location & languages ─── */}
                    <div className="enroll-section">
                        <div className="enroll-section-title">
                            <span className="enroll-section-num">2</span>
                            <h2>Location &amp; languages</h2>
                        </div>
                        <div className="enroll-row-3">
                            <div className="enroll-field">
                                <label>Country</label>
                                <input type="text" value={form.location.country}
                                       onChange={(e) => setLoc('country', e.target.value)} placeholder="India" />
                            </div>
                            <div className="enroll-field">
                                <label>State / Province</label>
                                <input type="text" value={form.location.state}
                                       onChange={(e) => setLoc('state', e.target.value)} placeholder="Karnataka" />
                            </div>
                            <div className="enroll-field">
                                <label>City</label>
                                <input type="text" value={form.location.city}
                                       onChange={(e) => setLoc('city', e.target.value)} placeholder="Mysore" />
                            </div>
                        </div>
                        <div className="enroll-field">
                            <label>Languages</label>
                            <div className="enroll-tag-input">
                                {form.languages.map((l) => (
                                    <span key={l} className="enroll-tag">
                                        {l}<button type="button" onClick={() => removeTag('languages', l)}>×</button>
                                    </span>
                                ))}
                                <input type="text" value={tagInput.languages}
                                       onChange={(e) => setTagInput({ ...tagInput, languages: e.target.value })}
                                       onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag('languages'); } }}
                                       placeholder={form.languages.length ? '' : 'Sanskrit, Kannada, English… (Enter to add)'} />
                            </div>
                        </div>
                    </div>

                    {/* ─── 3. Tradition (optional) ─── */}
                    <div className="enroll-section">
                        <div className="enroll-section-title">
                            <span className="enroll-section-num">3</span>
                            <h2>Tradition <span className="hint">(optional, for tradition-aligned roles)</span></h2>
                        </div>
                        <div className="enroll-row">
                            <div className="enroll-field">
                                <label>Veda-śākhā</label>
                                <input type="text" value={form.veda_shakha}
                                       onChange={(e) => set('veda_shakha', e.target.value)}
                                       placeholder="e.g., Kṛṣṇa Yajur · Taittirīya" />
                            </div>
                            <div className="enroll-field">
                                <label>Sampradāya</label>
                                <input type="text" value={form.sampradaya}
                                       onChange={(e) => set('sampradaya', e.target.value)}
                                       placeholder="Smārta, Śrī-vaiṣṇava, Mādhva …" />
                            </div>
                        </div>
                        <div className="enroll-row">
                            <div className="enroll-field">
                                <label>Paramparā</label>
                                <input type="text" value={form.parampara}
                                       onChange={(e) => set('parampara', e.target.value)}
                                       placeholder="Guru-paramparā" />
                            </div>
                            <div className="enroll-field">
                                <label>Gotra</label>
                                <input type="text" value={form.gotra}
                                       onChange={(e) => set('gotra', e.target.value)}
                                       placeholder="Bhāradvāja" />
                            </div>
                        </div>
                    </div>

                    {/* ─── 4. Education ─── */}
                    <div className="enroll-section">
                        <div className="enroll-section-title">
                            <span className="enroll-section-num">4</span>
                            <h2>Education</h2>
                        </div>
                        <div className="enroll-row">
                            <div className="enroll-field">
                                <label>Highest qualification</label>
                                <select value={form.highest_qualification}
                                        onChange={(e) => set('highest_qualification', e.target.value)}>
                                    <option value="">— select —</option>
                                    {QUAL_OPTIONS.map((q) => <option key={q} value={q}>{q}</option>)}
                                </select>
                            </div>
                            <div className="enroll-field">
                                <label>Field of study</label>
                                <input type="text" value={form.field_of_study}
                                       onChange={(e) => set('field_of_study', e.target.value)}
                                       placeholder="Sanskrit, CS, Āyurveda…" />
                            </div>
                        </div>
                        <div className="enroll-row">
                            <div className="enroll-field">
                                <label>Institution</label>
                                <input type="text" value={form.institution}
                                       onChange={(e) => set('institution', e.target.value)}
                                       placeholder="University / Pāṭhaśālā" />
                            </div>
                            <div className="enroll-field">
                                <label>Year completed</label>
                                <input type="number" min="1950" max="2099" value={form.year_completed}
                                       onChange={(e) => set('year_completed', e.target.value)} />
                            </div>
                        </div>
                    </div>

                    {/* ─── 5. Professional ─── */}
                    <div className="enroll-section">
                        <div className="enroll-section-title">
                            <span className="enroll-section-num">5</span>
                            <h2>Professional</h2>
                        </div>
                        <div className="enroll-row">
                            <div className="enroll-field">
                                <label>Current status</label>
                                <select value={form.current_status}
                                        onChange={(e) => set('current_status', e.target.value)}>
                                    {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                                </select>
                            </div>
                            <div className="enroll-field">
                                <label>Years of experience</label>
                                <input type="number" min="0" max="80" value={form.years_experience}
                                       onChange={(e) => set('years_experience', e.target.value)} />
                            </div>
                        </div>
                        <div className="enroll-field">
                            <label>Headline <span className="hint">(one-line summary)</span></label>
                            <input type="text" value={form.headline}
                                   onChange={(e) => set('headline', e.target.value)}
                                   placeholder="Yajur-veda Ghanapāṭhī · 8 years teaching" />
                        </div>
                        <div className="enroll-field">
                            <label>Bio</label>
                            <textarea value={form.bio} onChange={(e) => set('bio', e.target.value)}
                                      placeholder="Background, paramparā, notable work, languages of teaching…" />
                        </div>
                        <div className="enroll-field">
                            <label>Skills <span className="hint">(Enter to add)</span></label>
                            <div className="enroll-tag-input">
                                {form.skills.map((s) => (
                                    <span key={s} className="enroll-tag">
                                        {s}<button type="button" onClick={() => removeTag('skills', s)}>×</button>
                                    </span>
                                ))}
                                <input type="text" value={tagInput.skills}
                                       onChange={(e) => setTagInput({ ...tagInput, skills: e.target.value })}
                                       onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag('skills'); } }}
                                       placeholder={form.skills.length ? '' : 'Bhāṣya, Editing, Software, …'} />
                            </div>
                        </div>
                        <div className="enroll-row">
                            <div className="enroll-field">
                                <label>Resume URL</label>
                                <input type="url" value={form.resume_url}
                                       onChange={(e) => set('resume_url', e.target.value)}
                                       placeholder="https://… (PDF link or drive)" />
                            </div>
                            <div className="enroll-field">
                                <label>LinkedIn</label>
                                <input type="url" value={form.linkedin_url}
                                       onChange={(e) => set('linkedin_url', e.target.value)}
                                       placeholder="https://linkedin.com/in/…" />
                            </div>
                        </div>
                    </div>

                    {/* ─── 6. Preferences ─── */}
                    <div className="enroll-section">
                        <div className="enroll-section-title">
                            <span className="enroll-section-num">6</span>
                            <h2>Preferences</h2>
                        </div>
                        <div className="enroll-field">
                            <label>Open to <span className="hint">(select all that apply)</span></label>
                            <div className="enroll-pill-group">
                                {JOB_TYPE_PILLS.map((t) => (
                                    <button type="button" key={t}
                                            className={`enroll-pill ${form.job_types.includes(t) ? 'selected' : ''}`}
                                            onClick={() => togglePill('job_types', t)}>
                                        {t}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="enroll-row-3">
                            <div className="enroll-field">
                                <label>Available from</label>
                                <input type="date" value={form.available_from}
                                       onChange={(e) => set('available_from', e.target.value)} />
                            </div>
                            <div className="enroll-field">
                                <label>Min compensation</label>
                                <input type="number" min="0" value={form.min_compensation}
                                       onChange={(e) => set('min_compensation', e.target.value)}
                                       placeholder="0 = no minimum" />
                            </div>
                            <div className="enroll-field">
                                <label>Currency</label>
                                <select value={form.compensation_currency}
                                        onChange={(e) => set('compensation_currency', e.target.value)}>
                                    <option value="INR">INR</option>
                                    <option value="USD">USD</option>
                                    <option value="GBP">GBP</option>
                                    <option value="EUR">EUR</option>
                                    <option value="AUD">AUD</option>
                                    <option value="SGD">SGD</option>
                                    <option value="AED">AED</option>
                                </select>
                            </div>
                        </div>
                        <div className="enroll-checkbox-row">
                            <input type="checkbox" id="will_relocate" checked={form.willing_to_relocate}
                                   onChange={(e) => set('willing_to_relocate', e.target.checked)} />
                            <label htmlFor="will_relocate">Willing to relocate</label>
                        </div>
                        <div className="enroll-checkbox-row">
                            <input type="checkbox" id="will_remote" checked={form.willing_to_remote}
                                   onChange={(e) => set('willing_to_remote', e.target.checked)} />
                            <label htmlFor="will_remote">Open to remote work</label>
                        </div>
                    </div>

                    {/* ─── 7. Privacy & terms ─── */}
                    <div className="enroll-section">
                        <div className="enroll-section-title">
                            <span className="enroll-section-num">7</span>
                            <h2>Photo &amp; consent</h2>
                        </div>
                        <div className="enroll-field">
                            <label>Photo URL <span className="hint">(optional)</span></label>
                            <input type="url" value={form.photo_url}
                                   onChange={(e) => set('photo_url', e.target.value)}
                                   placeholder="https://…" />
                        </div>
                        <div className="enroll-checkbox-row">
                            <input type="checkbox" id="photo_consent" checked={form.photo_consent}
                                   onChange={(e) => set('photo_consent', e.target.checked)} />
                            <label htmlFor="photo_consent">
                                I consent to my photo being shown on my <strong>public profile</strong>.
                                Without consent, my profile shows initials only.
                            </label>
                        </div>
                        <div className="enroll-checkbox-row">
                            <input type="checkbox" id="terms" checked={form.accepts_terms}
                                   onChange={(e) => set('accepts_terms', e.target.checked)} required />
                            <label htmlFor="terms">
                                I confirm the information above is accurate and accept the
                                <a href="#" style={{ marginLeft: 4 }}>Terms</a> and <a href="#">Privacy Policy</a>.
                            </label>
                        </div>
                    </div>

                    {success && <div className="enroll-success"><Icon name="check" size={16} /> {success}</div>}
                    {error   && <div className="enroll-error">{error}</div>}

                    <div className="enroll-submit-row">
                        <span className="left">Auto-saved when you submit · revisit any time to edit.</span>
                        <div className="right">
                            <button type="button" className="sw-btn sw-btn-ghost" onClick={() => navigate('/')}>
                                Cancel
                            </button>
                            <button type="submit" className="sw-btn sw-btn-saffron sw-btn-lg" disabled={submitting}>
                                {submitting ? 'Saving…' : 'Save profile'}
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EnrollCandidate;
